import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";

import type { RootState } from "../../../store/store";
// Interfaces
import type {
  NotificationTrackerItem,
  NotificationTrackerPayload,
} from "@/features/admin/types/customerNotification.types";
// Assets
import type { RefreshTimePayload } from "@/shared/types/common.types";
import { commonService } from "@/services/commonService";
import { configService } from "@/features/admin/services/configService";
import { ls } from "@/shared/utils/localStorage";
import { debounce } from "@/shared/utils/debounce";
import type { ConfigState } from "../types/config.types";

const initialState: ConfigState = {
  NotifyTrackerAlertNamesDst: [],
  NotifyTrackerDetailsDst: [],
  NotifyTrackerEmailStatsDst: {
    total: 0,
    sent: 0,
    delivered: 0,
    failed: 0,
    retrying: 0,
    processing: 0,
    pending: 0,
  },
  _lastRefreshedTimeDst: null,
  status: {
    StatusDst: "idle",
  },
  filters: {
    customer: ls.get<string[]>("SelectedNotifyTrackerCustomer", []),
    notifyTimeRange: localStorage.getItem("SubscribeTimeRange") ?? "Past 24 Hrs",
    notifyFrom: null,
    notifyTo: null,
  },
  filtersInitialised: true,
};

export const fetchNotifyAlertNames = createAsyncThunk(
  "data/fetchNotifyAlertNames",
  async () => {
    const res = await configService.GetAlertNames();
    return Array.from(
      new Set(res.map((a: any) => a.alertName).filter(Boolean)),
    ).sort((a: string, b: string) => a.localeCompare(b));
  },
);

export const fetchNotifyTrackerDetails = createAsyncThunk(
  "data/fetchNotifyTrackerDetails",
  async (payload: NotificationTrackerPayload) => {
    const res = await configService.GetNotificationTrackerDetails(payload);
    return res;
  },
);

export const fetchLastRefreshTime = createAsyncThunk(
  "data/fetchLastRefreshTime",
  async (payload: RefreshTimePayload, { signal, rejectWithValue }) => {
    try {
      const res = await commonService.GetLastRefreshedTime(
        payload.currentPage,
        payload.timezone,
        { signal },
      );
      const dateString = res.lastRefreshedTimeUtc?.toString();
      localStorage.setItem("LastRefresh", dateString!);
      return res.lastRefreshedTimeUtc;
    } catch (err: any) {
      if (err.name === "AbortError") {
        return rejectWithValue("aborted");
      }
      throw err;
    }
  },
);

// Slice
const configSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters(state) {
      state.filters = {
        customer: [],
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifyAlertNames.fulfilled, (state, action) => {
        state.NotifyTrackerAlertNamesDst = action.payload as string[];
        state.status.StatusDst = "succeeded";
      })
      .addCase(fetchNotifyAlertNames.rejected, (state) => {
        state.status.StatusDst = "failed";
      })
      .addCase(fetchLastRefreshTime.fulfilled, (state, action) => {
        state._lastRefreshedTimeDst = action.payload!;
      })
      .addCase(fetchNotifyTrackerDetails.pending, (state) => {
        // no status field dedicated; keep simple
      })
      .addCase(fetchNotifyTrackerDetails.fulfilled, (state, action) => {
        const payload = action.payload || ({} as any);
        const items: NotificationTrackerItem[] =
          payload.trackerDetailsResults ?? [];
        state.NotifyTrackerDetailsDst = items;
        const total = payload.total ?? 0;
        const sent = payload.sent ?? 0;
        const delivered = payload.delivered ?? 0;
        const failed = Math.max(0, sent - delivered);
        // derived detailed counts from items
        const counts = items.reduce(
          (acc, it) => {
            acc.total++;
            switch (it.emailStatus) {
              case "Sent":
                acc.sent++;
                break;
              case "Failed":
              case "DeadLettered":
                acc.failed++;
                break;
              case "Retrying":
                acc.retrying++;
                break;
              case "Processing":
                acc.processing++;
                break;
              case "Pending":
                acc.pending++;
                break;
            }
            return acc;
          },
          {
            total: 0,
            sent: 0,
            failed: 0,
            retrying: 0,
            processing: 0,
            pending: 0,
          },
        );

        state.NotifyTrackerEmailStatsDst = {
          total,
          sent,
          delivered,
          failed,
          retrying: counts.retrying,
          processing: counts.processing,
          pending: counts.pending,
        };
      })
      .addCase(fetchNotifyTrackerDetails.rejected, (state) => {
        // clear on error
        state.NotifyTrackerDetailsDst = [];
        state.NotifyTrackerEmailStatsDst = {
          total: 0,
          sent: 0,
          delivered: 0,
          failed: 0,
          retrying: 0,
          processing: 0,
          pending: 0,
        };
      });
  },
});

export const { setFilters, clearFilters } = configSlice.actions;
export default configSlice.reducer;

/// --- Debounced severity filter dispatcher ---
let debouncedDispatch: ((sev: string[], dispatch: any) => void) | null = null;

export const setSeverityFiltersDebounced =
  (sev: string[]) => (dispatch: any) => {
    if (!debouncedDispatch) {
      debouncedDispatch = debounce((s: string[], d: any) => {
        d(setFilters({ severity: s }));
      }, 100);
    }
    debouncedDispatch(sev, dispatch);
  };

// --- Selectors ---
export const selectFilters = (state: RootState) => state.config.filters;

export const selectFiltersInitialised = (state: RootState) =>
  state.config.filtersInitialised;

export const selectNotifyAlertNames = (state: RootState) =>
  state.config.NotifyTrackerAlertNamesDst;
export const selectNotifyTrackerDetails = (state: RootState) =>
  state.config.NotifyTrackerDetailsDst;
export const selectNotifyTrackerEmailStats = (state: RootState) =>
  state.config.NotifyTrackerEmailStatsDst;

// --- Suppressed Alerts data ---

// export const suppressAlertsData = createSelector(
//   [(state: RootState) => state.data.SuppressedAlertsDst],
//   ()
// )
